import { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip, Popup, GeoJSON, useMap } from "react-leaflet";
import L from "leaflet";
import { api } from "@/lib/api";
import { useScenario } from "@/contexts/ScenarioContext";
import { useI18n } from "@/contexts/I18nContext";
import { track, EVENTS } from "@/lib/analytics";
import { Zap, Gauge, ShieldCheck, Activity } from "lucide-react";
import { toast } from "sonner";
import "leaflet/dist/leaflet.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const COLOR = { healthy: "#10B981", warning: "#F59E0B", down: "#EF4444" };

// Official PE municipalities GeoJSON (IBGE-based via tbrugz/geodata-br)
const PE_GEOJSON_URL = "https://raw.githubusercontent.com/tbrugz/geodata-br/master/geojson/geojs-26-mun.json";

const HIGHLIGHT_MUNICIPALITIES = new Set([
  "recife",
  "caruaru",
  "limoeiro",
  "toritama",
  "garanhuns",
  "arcoverde",
  "serra talhada",
  "salgueiro",
  "cabrobo",
  "araripina",
  "belem do sao francisco",
]);

// localStorage cache for the PE GeoJSON (~5MB). Bumped key when format changes.
const GEOJSON_CACHE_KEY = "evos_pe_geojson_v1";
const GEOJSON_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function normalize(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z\s]/g, "")
    .trim();
}

function Recenter({ center }) {
  const map = useMap();
  useEffect(() => { map.setView(center, map.getZoom()); }, [center, map]);
  return null;
}

export default function GeoMap() {
  const { scenario } = useScenario();
  const { t, locale } = useI18n();
  const [units, setUnits] = useState([]);
  const [geo, setGeo] = useState(null);
  const [simulating, setSimulating] = useState(null);

  useEffect(() => {
    if (!scenario) return;
    api.get(`/geo/units?scenario=${scenario.id}`).then(({ data }) => setUnits(data));
  }, [scenario]);

  // Load PE municipalities GeoJSON (localStorage cache, fall back to remote)
  useEffect(() => {
    let cancelled = false;
    // Try cache first
    try {
      const cached = localStorage.getItem(GEOJSON_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.ts && Date.now() - parsed.ts < GEOJSON_CACHE_TTL_MS && parsed.data) {
          setGeo(parsed.data);
          return;
        }
      }
    } catch { /* localStorage may be unavailable in private mode */ }

    fetch(PE_GEOJSON_URL)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setGeo(data);
        try {
          localStorage.setItem(GEOJSON_CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
        } catch (_) { /* quota exceeded — silently ignore */ }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const simulateLinkDrop = async (u) => {
    setSimulating(u.id);
    track(EVENTS.SIMULATE_LINK_DROP, { unit_id: u.id, unit_name: u.short, source: "geo_map" });
    try {
      await api.post("/simulate/link-drop", { unit_id: u.id });
      await new Promise((r) => setTimeout(r, 800));
      const run = await api.post(`/playbooks/pb_failover_link/run`);
      toast.success(locale === "pt" ? "Failover orquestrado" : "Failover orchestrated", {
        description: locale === "pt"
          ? `Tráfego redirecionado via Vivo para ${u.short} · ${run.data.log.length} passos OK`
          : `Traffic rerouted via Vivo for ${u.short} · ${run.data.log.length} steps OK`,
      });
    } catch {
      toast.error(locale === "pt" ? "Falha na simulação" : "Simulation failed");
    } finally {
      setSimulating(null);
    }
  };

  const styleMunicipality = (feature) => {
    const name = normalize(feature?.properties?.name);
    const isHighlight = HIGHLIGHT_MUNICIPALITIES.has(name);
    return {
      color: isHighlight ? "#3B82F6" : "#334155",
      weight: isHighlight ? 1.4 : 0.5,
      opacity: isHighlight ? 0.95 : 0.55,
      fillColor: isHighlight ? "#1E3A8A" : "#1E293B",
      fillOpacity: isHighlight ? 0.35 : 0.12,
    };
  };

  const center = [-8.3, -37.5];

  return (
    <div className="relative" data-testid="geo-map">
      <MapContainer
        center={center}
        zoom={7}
        scrollWheelZoom={false}
        style={{ height: 460, width: "100%", background: "#020617" }}
        attributionControl={false}
      >
        <Recenter center={center} />
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          subdomains="abcd"
          maxZoom={19}
        />
        {geo && (
          <GeoJSON key="pe-municipalities" data={geo} style={styleMunicipality} />
        )}
        {units.map((u) => {
          const color = COLOR[u.status] || "#3B82F6";
          return (
            <TaggedCircleMarker
              key={u.id}
              testId={`geo-pin-${u.id}`}
              center={[u.lat, u.lng]}
              radius={u.role === "HQ" ? 12 : 10}
              pathOptions={{ color: "#020617", weight: 2, fillColor: color, fillOpacity: 0.95 }}
            >
              <Tooltip direction="top" offset={[0, -8]} opacity={0.95}>
                <div style={{ fontFamily: "IBM Plex Mono", fontSize: 11 }}>
                  <strong>{u.name}</strong><br/>
                  {u.short} · {u.status.toUpperCase()}
                </div>
              </Tooltip>
              <Popup minWidth={260}>
                <div style={{ minWidth: 240, color: "#F8FAFC" }}>
                  <div style={{ fontSize: 10, letterSpacing: "0.2em", color: "#94A3B8", textTransform: "uppercase", fontFamily: "IBM Plex Mono", marginBottom: 4 }}>
                    {u.role} · {u.short}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>{u.name}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
                    <Cell icon={Activity} label={locale === "pt" ? "Latência" : "Latency"} value={u.latency_ms === 0 ? "—" : `${u.latency_ms} ms`} />
                    <Cell icon={Gauge} label={locale === "pt" ? "Perda" : "Loss"} value={`${u.loss_pct}%`} />
                    <Cell icon={ShieldCheck} label="SLA" value={`${u.sla_pct}%`} />
                    <Cell label="Status" value={u.status.toUpperCase()} color={color} />
                  </div>
                  <button
                    data-testid={`geo-sim-drop-${u.id}`}
                    onClick={() => simulateLinkDrop(u)}
                    disabled={simulating === u.id}
                    style={{
                      width: "100%", background: "#F59E0B", color: "#020617", border: 0,
                      padding: "8px 12px", fontWeight: 700, fontSize: 11, letterSpacing: "0.1em",
                      textTransform: "uppercase", cursor: simulating === u.id ? "wait" : "pointer", borderRadius: 2,
                    }}
                  >
                    <Zap size={12} style={{ display: "inline", marginRight: 6 }} />
                    {simulating === u.id
                      ? (locale === "pt" ? "Simulando..." : "Simulating...")
                      : (locale === "pt" ? "Simular Queda de Link" : "Simulate Link Drop")}
                  </button>
                </div>
              </Popup>
            </TaggedCircleMarker>
          );
        })}
      </MapContainer>

      <div className="absolute bottom-3 left-3 z-[400] flex flex-wrap gap-2 text-[10px] font-mono">
        {[["healthy", "#10B981"], ["warning", "#F59E0B"], ["down", "#EF4444"]].map(([k, c]) => (
          <div key={k} className="flex items-center gap-1.5 border border-slate-800 bg-[#0F172A]/85 px-2 py-1 backdrop-blur">
            <span className="w-2 h-2 rounded-full" style={{ background: c }} />
            <span className="text-slate-400 uppercase tracking-wider">{t(`map.${k}`)}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5 border border-slate-800 bg-[#0F172A]/85 px-2 py-1 backdrop-blur">
          <span className="w-3 h-2 border border-[#3B82F6]" style={{ background: "rgba(30,58,138,0.35)" }} />
          <span className="text-slate-400 uppercase tracking-wider">
            {locale === "pt" ? "MUNICÍPIOS ATENDIDOS" : "SERVED MUNICIPALITIES"}
          </span>
        </div>
      </div>

      {!geo && (
        <div className="absolute top-3 right-3 z-[400] text-[10px] font-mono text-slate-500 border border-slate-800 bg-[#0F172A]/85 px-2 py-1 evos-blink">
          {locale === "pt" ? "Carregando municípios de PE..." : "Loading PE municipalities..."}
        </div>
      )}

      <style>{`
        .leaflet-popup-content-wrapper, .leaflet-popup-tip {
          background: #0F172A !important;
          border: 1px solid #1E293B !important;
          color: #F8FAFC !important;
          border-radius: 2px !important;
        }
        .leaflet-popup-content { margin: 12px 14px !important; }
        .leaflet-container a.leaflet-popup-close-button { color: #94A3B8 !important; }
      `}</style>
    </div>
  );
}

// CircleMarker wrapper that forwards data-testid to the underlying SVG path.
function TaggedCircleMarker({ testId, children, ...props }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && ref.current._path && testId) {
      ref.current._path.setAttribute("data-testid", testId);
    }
  });
  return <CircleMarker ref={ref} {...props}>{children}</CircleMarker>;
}

function Cell({ icon: Icon, label, value, color }) {
  return (
    <div style={{ border: "1px solid #1E293B", background: "#020617", padding: 8 }}>
      <div style={{ fontSize: 9, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.15em", fontFamily: "IBM Plex Mono", marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}>
        {Icon && <Icon size={10} />} {label}
      </div>
      <div style={{ fontFamily: "IBM Plex Mono", fontWeight: 700, color: color || "#F8FAFC", fontSize: 13 }}>{value}</div>
    </div>
  );
}
